import type { Color, TooltipLabelStyle } from 'chart.js';

/**
 * Chart types whose elements are points. Chart.js draws a tooltip swatch from the hovered
 * element's style, which for a point is its marker, so a hollow marker came out as an empty
 * ring next to the value, read as "no value".
 */
const POINT_TYPES = new Set(['line', 'scatter', 'bubble', 'radar']);

function isColor(value: unknown): value is Color {
    return (
        typeof value === 'string' ||
        (typeof CanvasGradient !== 'undefined' && value instanceof CanvasGradient) ||
        (typeof CanvasPattern !== 'undefined' && value instanceof CanvasPattern)
    );
}

function numberOr(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** The parts of a Chart.js tooltip item the swatch reads; a real `TooltipItem` is one. */
export interface SwatchItem {
    chart: {
        getDatasetMeta(datasetIndex: number): {
            type: string;
            controller: { getStyle(index: number, active: boolean): Record<string, unknown> };
        };
    };
    datasetIndex: number;
    dataIndex: number;
}

/**
 * The tooltip swatch of every chart: a point's swatch is filled with its outline colour, the
 * series colour, whatever its marker's fill; bars and slices keep the fill they are drawn
 * with, which can carry a threshold or an in-progress shade. Installed as the Chart.js default
 * in registerCharts, so charts built outside the metric chart builder get it too.
 */
export function solidSwatch(item: SwatchItem): TooltipLabelStyle {
    const meta = item.chart.getDatasetMeta(item.datasetIndex);
    const style = meta.controller.getStyle(item.dataIndex, false);
    const border = isColor(style.borderColor) ? style.borderColor : 'transparent';
    const fill = isColor(style.backgroundColor) ? style.backgroundColor : border;
    // A point drawn without an outline keeps its fill, which is then its only colour.
    const solid = POINT_TYPES.has(meta.type) && border !== 'transparent' ? border : fill;
    return {
        borderColor: border,
        backgroundColor: solid,
        borderWidth: numberOr(style.borderWidth, 0),
        borderRadius: 0,
    };
}
